import { createContext, useContext, useState, useEffect } from 'react';
import PurchaseService from '~/services/purchase.service';
import CourseService from '~/services/courses.service';
import type { ReactNode } from 'react';
import type { Course } from '~/types';
import { useAuth } from '~/context/auth';

interface PurchaseItem {
  course: Course;
  addedAt: Date;
}

interface PurchaseContextType {
  items: PurchaseItem[];
  isModalOpen: boolean;
  addToCart: (course: Course) => void;
  removeFromCart: (courseId: string) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  getTotalItems: () => number;
  buyCourse: (course: Course) => void;
}

const PurchaseContext = createContext<PurchaseContextType | undefined>(undefined);

const PURCHASE_STORAGE_KEY = 'chronomed_purchase';

type CartCourseStored = Pick<
  Course,
  | 'id'
  | 'title'
  | 'description'
  | 'price'
  | 'price_cents'
  | 'thumbnail_url'
  | 'lessons_count'
  | 'duration_minutes'
  | 'category'
>;

function courseToStored(course: Course): CartCourseStored {
  return {
    id: course.id,
    title: course.title,
    description: course.description,
    price: course.price,
    price_cents: course.price_cents,
    thumbnail_url: course.thumbnail_url,
    lessons_count: course.lessons_count,
    duration_minutes: course.duration_minutes,
    category: course.category,
  };
}

function saveCartToStorage(items: PurchaseItem[]) {
  const toStore = items.map((item) => ({
    course: courseToStored(item.course),
    addedAt: item.addedAt.toISOString(),
  }));
  try {
    localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(toStore));
  } catch (error) {
    console.error('Erro ao salvar carrinho no storage:', error);
  }
}

export function PurchaseProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, login, user } = useAuth();
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const savedCart = localStorage.getItem(PURCHASE_STORAGE_KEY);
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart) as { course: CartCourseStored; addedAt: string }[];
        setItems(
          parsed.map((item) => ({
            course: item.course as Course,
            addedAt: new Date(item.addedAt),
          })),
        );
      } catch (error) {
        console.error('Erro ao carregar carrinho:', error);
      }
    }
  }, []);

  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const removePurchasedCourses = async () => {
      try {
        const { data: myCourses } = await CourseService.getMyCourses();
        const purchasedCourseIds = new Set(myCourses.map((course) => course.id));

        setItems((prev) => {
          const filtered = prev.filter((item) => !purchasedCourseIds.has(item.course.id));

          return filtered;
        });
      } catch {
        setItems([]);
      }
    };

    removePurchasedCourses();
  }, [isAuthenticated, user]);

  const addToCart = (course: Course) => {
    setItems((prev) => {
      const exists = prev.find((item) => item.course.id === course.id);
      if (exists) {
        console.log('Curso já está no carrinho');
        return prev;
      }

      const newItem: PurchaseItem = {
        course,
        addedAt: new Date(),
      };

      return [...prev, newItem];
    });
  };

  const removeFromCart = (courseId: string) => {
    setItems((prev) => prev.filter((item) => item.course.id !== courseId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const openCart = () => {
    setIsModalOpen(true);
  };

  const closeCart = () => {
    setIsModalOpen(false);
  };

  const getTotalItems = () => {
    return items.length;
  };

  const buyCourse = async (course: Course) => {
    if (!isAuthenticated) {
      login();
      return;
    }

    try {
      const response = await PurchaseService.createPurchase([course.id]);
      setItems((old) => old.filter((item) => item.course.id !== course.id));
      window.open(response.paymentLink.url, '_blank');
    } catch (error) {
      console.error('Erro ao comprar curso:', error);
    }
  };

  const value: PurchaseContextType = {
    items,
    isModalOpen,
    addToCart,
    removeFromCart,
    clearCart,
    openCart,
    closeCart,
    getTotalItems,
    buyCourse,
  };

  return <PurchaseContext.Provider value={value}>{children}</PurchaseContext.Provider>;
}

export function useCart() {
  const context = useContext(PurchaseContext);
  if (context === undefined) {
    throw new Error('useCart deve ser usado dentro de um CartProvider');
  }
  return context;
}
